import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { teacherApi, API } from "@/lib/api";
import { isJustFixed } from "@/lib/justFixed";
import TeacherHeader from "./TeacherHeader";
import { ChemText } from "@/components/ChemText";
import { Plus, Trash2, Sparkles, Search, Pencil, ImagePlus, X as XIcon, Info, FileDown, Gift } from "lucide-react";

const EMPTY = {
  type: "mc", topic: "", text: "",
  options: ["", "", "", ""], correct: "A",
  image_url: "", explanation: "", scope: "basic_1",
};

const SCOPE_LABELS = {
  basic_1: "Klasa 1 — podstawowy",
  basic_2: "Klasa 2 — podstawowy",
  basic_3: "Klasa 3 — podstawowy",
  extended_1: "Klasa 1 — rozszerzony",
  extended_2: "Klasa 2 — rozszerzony",
  extended_3: "Klasa 3 — rozszerzony",
  extended_4: "Klasa 4 — rozszerzony",
};

export default function Questions() {
  // Initialize `quality` filter from URL param (e.g. deep-link from Admin
  // Panel "Kolejka do przeglądu" widget → /teacher/questions?quality=pending).
  const [searchParams] = useSearchParams();
  const initialQuality = ["all", "pending", "warning", "ok"]
    .includes(searchParams.get("quality"))
    ? searchParams.get("quality") : "all";

  const [list, setList] = useState([]);
  const [topics, setTopics] = useState([]);
  const [scopes, setScopes] = useState([]); // [{scope,label,question_count,active,subscription}]
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterTopic, setFilterTopic] = useState("all");
  const [filterScope, setFilterScope] = useState("all");
  const [filterQuality, setFilterQuality] = useState(initialQuality);
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeForm, setProposeForm] = useState(EMPTY);
  const [proposeBusy, setProposeBusy] = useState(false);
  // If set, this proposal is a CORRECTION to a shared question (original id + snapshot).
  const [correctionOf, setCorrectionOf] = useState(null); // {id, originalText}
  const [correctionNote, setCorrectionNote] = useState("");
  // AI synonym suggestion (admin-only, in edit dialog for open questions)
  const [synonymsBusy, setSynonymsBusy] = useState(false);
  const [synonymsSuggestion, setSynonymsSuggestion] = useState(null); // [{text, selected}]

  const suggestSynonyms = async () => {
    if (!editing) return;
    setSynonymsBusy(true);
    try {
      const { data } = await teacherApi.post(`/admin/questions/${editing}/suggest-synonyms`);
      const items = (data?.suggested || []).map((s) => ({ text: s, selected: true }));
      setSynonymsSuggestion(items);
      if (!items.length) toast.info("AI nie znalazł dodatkowych synonimów");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd analizy AI");
    } finally { setSynonymsBusy(false); }
  };

  const toggleSynonym = (i) => {
    setSynonymsSuggestion((cur) => cur.map((s, idx) => idx === i ? { ...s, selected: !s.selected } : s));
  };

  const applySelectedSynonyms = () => {
    const picks = (synonymsSuggestion || []).filter((s) => s.selected).map((s) => s.text);
    if (!picks.length) { toast.info("Nie wybrano żadnego synonimu"); return; }
    const current = (form.correct || "").trim();
    // Deduplicate — don't append if already present
    const existingLower = current.toLowerCase();
    const newOnes = picks.filter((p) => !existingLower.includes(p.toLowerCase()));
    if (!newOnes.length) { toast.info("Wszystkie wybrane synonimy już są w polu"); return; }
    const joined = current ? `${current} | ${newOnes.join(" | ")}` : newOnes.join(" | ");
    setForm({ ...form, correct: joined });
    setSynonymsSuggestion(null);
    toast.success(`Dodano ${newOnes.length} synonimów`);
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [ai, setAi] = useState({ topic: "ogólne", count: 5, type: "mixed", scope: "basic_1" });
  const [aiLoading, setAiLoading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [role, setRole] = useState(null);
  const [myId, setMyId] = useState(null);

  useEffect(() => {
    teacherApi.get("/auth/me").then(({ data }) => {
      setRole(data?.role);
      setMyId(data?.id);
    }).catch(() => {});
  }, []);

  const canEdit = (q) => {
    if (role === "admin") return true;
    return q?.owner_type === "teacher" && q?.owner_id === myId;
  };

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterScope !== "all") params.set("scope", filterScope);
    if (filterQuality !== "all") params.set("quality", filterQuality);
    const qs = params.toString();
    const [q, t, s] = await Promise.all([
      teacherApi.get(`/questions${qs ? `?${qs}` : ""}`),
      teacherApi.get(`/questions/topics${filterScope !== "all" ? `?scope=${filterScope}` : ""}`),
      teacherApi.get("/scopes"),
    ]);
    setList(q.data);
    setTopics(t.data.topics || []);
    setScopes(s.data.scopes || []);
  }, [filterScope, filterQuality]);
  useEffect(() => { load(); }, [load]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await teacherApi.post("/questions/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Obrazek przesłany");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd uploadu");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, scope: filterScope !== "all" ? filterScope : "basic_1" });
    setSynonymsSuggestion(null);
    setOpen(true);
  };
  const openEdit = (q) => {
    setEditing(q.id);
    setForm({
      type: q.type,
      topic: q.topic || "",
      text: q.text,
      options: q.options || ["", "", "", ""],
      correct: q.correct,
      image_url: q.image_url || "",
      explanation: q.explanation || "",
      scope: q.scope || "basic_1",
    });
    setSynonymsSuggestion(null);
    setOpen(true);
  };

  const save = async () => {
    if (!form.text.trim() || !form.correct.trim()) {
      toast.error("Uzupełnij treść i poprawną odpowiedź");
      return;
    }
    const payload = {
      type: form.type, topic: form.topic, text: form.text,
      options: form.type === "mc" ? form.options : null,
      correct: form.correct,
      image_url: form.image_url || null,
      explanation: form.explanation || null,
      scope: form.scope || "basic_1",
    };
    try {
      if (editing) {
        await teacherApi.put(`/questions/${editing}`, payload);
        toast.success("Pytanie zaktualizowane");
      } else {
        await teacherApi.post("/questions", payload);
        toast.success("Pytanie dodane");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd zapisu");
    }
  };

  const submitProposal = async () => {
    if (!proposeForm.text.trim() || !proposeForm.correct.trim()) {
      toast.error("Uzupełnij treść i poprawną odpowiedź");
      return;
    }
    if (proposeForm.type === "mc" && proposeForm.options.some((o) => !o.trim())) {
      toast.error("Uzupełnij wszystkie 4 opcje ABCD");
      return;
    }
    setProposeBusy(true);
    try {
      await teacherApi.post("/questions/propose", {
        type: proposeForm.type,
        topic: proposeForm.topic,
        text: proposeForm.text,
        options: proposeForm.type === "mc" ? proposeForm.options : null,
        correct: proposeForm.correct,
        explanation: proposeForm.explanation || null,
        scope: proposeForm.scope || "basic_1",
        correction_of: correctionOf?.id || null,
        correction_note: correctionOf ? correctionNote : null,
      });
      toast.success(correctionOf
        ? "Poprawka wysłana — admin ją przejrzy 🔧"
        : "Propozycja wysłana — admin ją przejrzy 👌");
      setProposeOpen(false);
      setProposeForm(EMPTY);
      setCorrectionOf(null);
      setCorrectionNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd zapisu propozycji");
    } finally { setProposeBusy(false); }
  };

  const openCorrection = (q) => {
    // Pre-fill the propose form with the ORIGINAL question data so the teacher
    // can tweak instead of retyping from scratch.
    setCorrectionOf({ id: q.id, originalText: q.text });
    setCorrectionNote("");
    setProposeForm({
      type: q.type,
      topic: q.topic || "",
      text: q.text || "",
      options: q.options && q.options.length === 4 ? [...q.options] : ["", "", "", ""],
      correct: q.correct || "",
      explanation: q.explanation || "",
      image_url: "",
      scope: q.scope || "basic_1",
    });
    setProposeOpen(true);
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await teacherApi.delete(`/questions/${toDelete.id}`);
      toast.success("Pytanie usunięte");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć");
    } finally {
      setToDelete(null);
    }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await teacherApi.post("/questions/generate-ai", {
        topic: ai.topic, count: Number(ai.count), type: ai.type, scope: ai.scope,
      });
      toast.success(`Utworzono ${data.created} pytań`);
      setAiOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd generowania");
    } finally {
      setAiLoading(false);
    }
  };

  const downloadPoolPDF = async () => {
    const params = new URLSearchParams();
    if (filterScope !== "all") params.set("scope", filterScope);
    if (filterTopic !== "all") params.set("topic", filterTopic);
    if (filterType !== "all") params.set("q_type", filterType);
    if (filterQuality !== "all") params.set("quality", filterQuality);
    if (search.trim()) params.set("search", search.trim());
    const qs = params.toString();
    try {
      const res = await teacherApi.get(`/questions/pool-pdf${qs ? `?${qs}` : ""}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to extract filename from Content-Disposition; fall back to default.
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "pula-pytan.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF pobrany");
    } catch (e) {
      const msg = e.response?.status === 404
        ? "Brak pytań dla wybranych filtrów"
        : (e.response?.data?.detail || "Błąd pobierania PDF");
      toast.error(msg);
    }
  };

  const filtered = list.filter((q) => {
    const matchType = filterType === "all" || q.type === filterType;
    const matchTopic = filterTopic === "all" || q.topic === filterTopic;
    const matchSearch = !search ||
      q.text.toLowerCase().includes(search.toLowerCase()) ||
      (q.topic || "").toLowerCase().includes(search.toLowerCase());
    return matchType && matchTopic && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <TeacherHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] mb-2">Baza pytań</div>
            <h1 className="font-display font-black text-4xl">Pytania ({list.length})</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            {role === "admin" && (
              <Button
                onClick={downloadPoolPDF}
                className="brut brut-hover bg-white hover:bg-white text-[#0F172A] font-bold rounded-md border-2 border-[#0F172A]"
                data-testid="download-pool-pdf-btn"
                title="PDF zawiera tylko pytania widoczne po zastosowaniu filtrów"
              >
                <FileDown className="w-4 h-4 mr-2" strokeWidth={2.5} />Pobierz pulę PDF ({filtered.length})
              </Button>
            )}
            {role === "admin" && (
              <Button
                onClick={() => setAiOpen(true)}
                className="brut brut-hover bg-[#E0E7FF] hover:bg-[#E0E7FF] text-[#0F172A] font-bold rounded-md"
                data-testid="generate-ai-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />Generuj AI
              </Button>
            )}
            <Button
              onClick={openNew}
              className="brut brut-hover bg-[#FEF08A] hover:bg-[#FEF08A] text-[#0F172A] font-bold rounded-md"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={2.5} />Dodaj pytanie
            </Button>
            {role !== "admin" && (
              <Button
                onClick={() => setProposeOpen(true)}
                className="brut brut-hover bg-[#D1FAE5] hover:bg-[#D1FAE5] text-[#065F46] font-bold rounded-md"
                data-testid="propose-question-btn"
                title="Wyślij pytanie do administratora — po akceptacji trafi do wspólnej puli"
              >
                <Gift className="w-4 h-4 mr-2" strokeWidth={2.5} />Zaproponuj do wspólnej puli
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              placeholder="Szukaj..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 brut-input border-2 border-[#0F172A] rounded-md h-11"
              data-testid="search-questions-input"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px] brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="filter-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
              <SelectItem value="all">Wszystkie typy</SelectItem>
              <SelectItem value="mc">ABCD</SelectItem>
              <SelectItem value="open">Otwarte</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTopic} onValueChange={setFilterTopic}>
            <SelectTrigger className="w-[220px] brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="filter-topic-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md max-h-[300px]">
              <SelectItem value="all">Wszystkie tematy</SelectItem>
              {topics.map((t) => (
                <SelectItem key={t.topic} value={t.topic}>{t.topic} ({t.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterScope} onValueChange={(v) => { setFilterScope(v); setFilterTopic("all"); }}>
            <SelectTrigger className="w-[240px] brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="filter-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md max-h-[300px]">
              <SelectItem value="all">Wszystkie zakresy</SelectItem>
              {scopes.map((s) => (
                <SelectItem key={s.scope} value={s.scope} data-testid={`filter-scope-option-${s.scope}`}>
                  {s.label} ({s.question_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {role === "admin" && (
            <Select value={filterQuality} onValueChange={setFilterQuality}>
              <SelectTrigger className="w-[200px] brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="filter-quality-select">
                <SelectValue placeholder="jakość — wszystkie" />
              </SelectTrigger>
              <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                <SelectItem value="all">jakość — wszystkie</SelectItem>
                <SelectItem value="pending" data-testid="filter-quality-pending">🕒 Do oceny</SelectItem>
                <SelectItem value="warning" data-testid="filter-quality-warning">⚠️ Wątpliwe</SelectItem>
                <SelectItem value="ok" data-testid="filter-quality-ok">✅ Zweryfikowane</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Question list */}
        <div className="space-y-3">
          {filtered.map((q, i) => (
            <div key={q.id} className="brut bg-white rounded-md p-4 flex items-start gap-4" data-testid={`question-${i}`}>
              {q.image_url && (
                <img
                  src={`${API.replace(/\/api$/, "")}${q.image_url}`}
                  alt=""
                  className="w-16 h-16 object-cover rounded-md border-2 border-[#0F172A] flex-shrink-0"
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`brut text-xs tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-md ${q.type === "mc" ? "bg-[#E0F2FE]" : "bg-[#D1FAE5]"}`}>
                    {q.type === "mc" ? "ABCD" : "Otwarte"}
                  </span>
                  <span className="brut text-xs tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-md bg-[#FEF08A]" data-testid={`question-scope-${i}`}>
                    {SCOPE_LABELS[q.scope || "basic_1"]}
                  </span>
                  {q.quality_warning && (
                    <span
                      className="brut text-xs tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-md bg-[#FECACA] text-[#7F1D1D]"
                      title={q.quality_warning}
                      data-testid={`question-warning-${i}`}
                    >
                      ⚠️ Wątpliwe
                    </span>
                  )}
                  {!q.quality_warning && isJustFixed(q.id) && (
                    <span
                      className="brut text-xs tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-md bg-[#BBF7D0] text-[#065F46] animate-pulse"
                      title="Ostrzeżenie AI wyczyszczone po zastosowaniu synonimów w tej sesji"
                      data-testid={`question-just-fixed-${i}`}
                    >
                      ✓ Właśnie naprawione
                    </span>
                  )}
                  {q.topic && (
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#64748B]">{q.topic}</span>
                  )}
                  {q.owner_type === "shared" && (
                    <span
                      className="brut text-xs tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-md bg-[#F1F5F9] text-[#334155]"
                      title="Pytanie ze wspólnej puli — edycja tylko przez administratora"
                      data-testid={`question-shared-${i}`}
                    >
                      🔒 Wspólne
                    </span>
                  )}
                </div>
                <div className="font-bold text-base"><ChemText text={q.text} /></div>
                {q.type === "mc" ? (
                  <div className="text-sm text-[#334155] mt-1">
                    {(q.options || []).map((opt, idx) => (
                      <div key={idx}>
                        <span className={q.correct === String.fromCharCode(65 + idx) ? "font-bold text-[#10B981]" : ""}>
                          {String.fromCharCode(65 + idx)}) <ChemText text={opt} />
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-[#10B981] font-bold mt-1">Odpowiedź: <ChemText text={q.correct} /></div>
                )}
                {q.explanation && (
                  <div className="text-xs text-[#64748B] italic mt-1 inline-flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span><ChemText text={q.explanation} /></span>
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                {canEdit(q) ? (
                  <>
                    <Button onClick={() => openEdit(q)} variant="ghost" size="sm" className="rounded-md" data-testid={`edit-question-${i}`}>
                      <Pencil className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                    <Button onClick={() => setToDelete(q)} variant="ghost" size="sm" className="rounded-md text-[#EF4444]" data-testid={`delete-question-${i}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col items-end gap-1 self-center">
                    <span
                      className="text-xs text-[#94A3B8] italic px-2"
                      title="Tylko administrator może edytować pytania ze wspólnej puli"
                      data-testid={`question-readonly-${i}`}
                    >
                      tylko do odczytu
                    </span>
                    {q.owner_type === "shared" && role !== "admin" && (
                      <Button
                        onClick={() => openCorrection(q)}
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7 px-2 rounded-md text-[#065F46] hover:bg-[#D1FAE5] font-bold"
                        title="Zaproponuj poprawkę tego pytania — trafi do administratora"
                        data-testid={`propose-correction-${i}`}
                      >
                        <Gift className="w-3 h-3 mr-1" strokeWidth={2.5} />
                        Zgłoś poprawkę
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="brut bg-white rounded-md border-2 border-[#0F172A] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display font-black text-2xl">
                {editing ? "Edytuj pytanie" : "Nowe pytanie"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-bold text-sm">Typ</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, correct: v === "mc" ? "A" : "" })}>
                    <SelectTrigger className="mt-2 brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="form-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                      <SelectItem value="mc">ABCD (jednokrotny wybór)</SelectItem>
                      <SelectItem value="open">Otwarte (krótka odpowiedź)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-bold text-sm">Temat</Label>
                  <Input
                    value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                    placeholder="np. Budowa atomu"
                    data-testid="form-topic-input"
                  />
                </div>
              </div>
              <div>
                <Label className="font-bold text-sm">Zakres</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger className="mt-2 brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="form-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                    {scopes.map((s) => (
                      <SelectItem key={s.scope} value={s.scope} data-testid={`form-scope-option-${s.scope}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-bold text-sm">Treść pytania</Label>
                <Textarea
                  value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                  className="mt-2 brut-input border-2 border-[#0F172A] rounded-md min-h-[80px]"
                  data-testid="form-text-input"
                />
                <div className="text-xs text-[#64748B] mt-1">
                  Wzory: <code className="bg-[#FEF08A] px-1 rounded">H_2O</code> → H₂O,
                  &nbsp;<code className="bg-[#FEF08A] px-1 rounded">{"Mg^{2+}"}</code> → Mg²⁺.
                  Podgląd: <span className="font-bold"><ChemText text={form.text} /></span>
                </div>
              </div>
              <div>
                <Label className="font-bold text-sm">Obrazek (opcjonalnie)</Label>
                {form.image_url ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={`${API.replace(/\/api$/, "")}${form.image_url}`}
                      alt=""
                      className="w-24 h-24 object-cover rounded-md border-2 border-[#0F172A]"
                    />
                    <Button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      variant="outline"
                      size="sm"
                      className="brut bg-white border-2 border-[#EF4444] text-[#EF4444] rounded-md"
                      data-testid="remove-image-btn"
                    >
                      <XIcon className="w-3 h-3 mr-1" strokeWidth={2.5} />Usuń obrazek
                    </Button>
                  </div>
                ) : (
                  <label className="mt-2 brut brut-hover bg-white border-2 border-dashed border-[#0F172A] rounded-md h-20 flex items-center justify-center gap-2 cursor-pointer font-bold text-sm" data-testid="upload-image-label">
                    <ImagePlus className="w-4 h-4" strokeWidth={2.5} />
                    {uploading ? "Przesyłam..." : "Wybierz plik (JPG/PNG/WEBP, max 4MB)"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                      data-testid="upload-image-input"
                    />
                  </label>
                )}
              </div>
              {form.type === "mc" ? (
                <div className="space-y-2">
                  <Label className="font-bold text-sm">Opcje (zaznacz poprawną)</Label>
                  {["A", "B", "C", "D"].map((letter, idx) => (
                    <div key={letter} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, correct: letter })}
                        className={`brut w-10 h-10 flex items-center justify-center rounded-md font-bold ${form.correct === letter ? "bg-[#10B981] text-white" : "bg-white"}`}
                        data-testid={`form-option-letter-${letter}`}
                      >
                        {letter}
                      </button>
                      <Input
                        value={form.options[idx]}
                        onChange={(e) => {
                          const opts = [...form.options];
                          opts[idx] = e.target.value;
                          setForm({ ...form, options: opts });
                        }}
                        className="brut-input border-2 border-[#0F172A] rounded-md h-10 flex-1"
                        data-testid={`form-option-input-${letter}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="font-bold text-sm">Poprawna odpowiedź</Label>
                    {editing && role === "admin" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={suggestSynonyms}
                        disabled={synonymsBusy}
                        className="text-xs h-7 px-2 rounded-md text-[#6B21A8] hover:bg-[#F3E8FF] font-bold"
                        data-testid="suggest-synonyms-btn"
                        title="Claude 4.6 zaproponuje akceptowane warianty odpowiedzi"
                      >
                        <Sparkles className="w-3 h-3 mr-1" strokeWidth={2.5} />
                        {synonymsBusy ? "Analizuję…" : "✨ Zaproponuj synonimy (AI)"}
                      </Button>
                    )}
                  </div>
                  <Input
                    value={form.correct} onChange={(e) => setForm({ ...form, correct: e.target.value })}
                    className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                    placeholder="np. H2O | woda | tlenek diwodoru"
                    data-testid="form-open-correct-input"
                  />
                  <div className="text-xs text-[#64748B] mt-1.5 leading-relaxed">
                    💡 Dopuść <b>kilka poprawnych odpowiedzi</b> — oddziel pionową kreską <code className="bg-[#F1F5F9] px-1 rounded">|</code> albo słowem
                    <code className="bg-[#F1F5F9] px-1 mx-1 rounded">lub</code>.
                    Przykład: <code className="bg-[#F1F5F9] px-1 rounded">Zasada wykluczania Pauliego | Zakaz Pauliego | Pauliego</code>
                  </div>
                  {synonymsSuggestion && (
                    <div className="mt-3 border-2 border-[#6B21A8] rounded-md bg-[#F3E8FF] p-3 space-y-2"
                         data-testid="synonyms-suggestion-panel">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-black uppercase tracking-wider text-[#6B21A8]">
                          ✨ Propozycje synonimów od AI
                        </div>
                        <button type="button" onClick={() => setSynonymsSuggestion(null)}
                                className="text-xs text-[#6B21A8] font-bold hover:underline"
                                data-testid="synonyms-close-btn">
                          Zamknij
                        </button>
                      </div>
                      {synonymsSuggestion.length ? (
                        <>
                          <div className="text-xs text-[#6B21A8]">Zaznacz warianty do zaakceptowania:</div>
                          <div className="space-y-1">
                            {synonymsSuggestion.map((syn, i) => (
                              <label key={i} className="flex items-center gap-2 text-sm bg-white p-2 rounded border border-[#6B21A8]"
                                     data-testid={`synonym-option-${i}`}>
                                <input type="checkbox" checked={syn.selected}
                                       onChange={() => toggleSynonym(i)}
                                       className="w-4 h-4" />
                                <span className="font-mono">{syn.text}</span>
                              </label>
                            ))}
                          </div>
                          <Button type="button" size="sm" onClick={applySelectedSynonyms}
                                  className="brut bg-[#6B21A8] text-white font-bold rounded-md w-full"
                                  data-testid="apply-synonyms-btn">
                            Dodaj wybrane do pola &bdquo;Poprawna odpowiedź&rdquo;
                          </Button>
                        </>
                      ) : (
                        <div className="text-xs text-[#6B21A8]">AI nie znalazł nowych synonimów dla tej odpowiedzi.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="font-bold text-sm">Wyjaśnienie (pokazane uczniowi po zakończeniu)</Label>
                <Textarea
                  value={form.explanation}
                  onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                  className="mt-2 brut-input border-2 border-[#0F172A] rounded-md min-h-[60px]"
                  placeholder="np. Atom tlenu ma 8 protonów i 8 elektronów."
                  data-testid="form-explanation-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={save}
                className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
                data-testid="save-question-btn"
              >
                Zapisz
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Dialog */}
        <Dialog open={aiOpen} onOpenChange={setAiOpen}>
          <DialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]">
            <DialogHeader>
              <DialogTitle className="font-display font-black text-2xl">Generuj pytania (AI)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-bold text-sm">Temat</Label>
                <Input
                  value={ai.topic} onChange={(e) => setAi({ ...ai, topic: e.target.value })}
                  className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                  placeholder="np. wiązania chemiczne, roztwory..."
                  data-testid="ai-topic-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-bold text-sm">Ile pytań</Label>
                  <Input
                    type="number" min={1} max={20}
                    value={ai.count} onChange={(e) => setAi({ ...ai, count: e.target.value })}
                    className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                    data-testid="ai-count-input"
                  />
                </div>
                <div>
                  <Label className="font-bold text-sm">Typ</Label>
                  <Select value={ai.type} onValueChange={(v) => setAi({ ...ai, type: v })}>
                    <SelectTrigger className="mt-2 brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="ai-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                      <SelectItem value="mixed">Mieszane</SelectItem>
                      <SelectItem value="mc">ABCD</SelectItem>
                      <SelectItem value="open">Otwarte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-bold text-sm">Zakres</Label>
                <Select value={ai.scope} onValueChange={(v) => setAi({ ...ai, scope: v })}>
                  <SelectTrigger className="mt-2 brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="ai-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                    {scopes.map((s) => (
                      <SelectItem key={s.scope} value={s.scope} data-testid={`ai-scope-option-${s.scope}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-[#64748B]">
                Pytania zostaną dodane do bazy. Możesz je edytować po wygenerowaniu.
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={generateAI}
                disabled={aiLoading}
                className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
                data-testid="confirm-generate-ai-btn"
              >
                {aiLoading ? "Generuję..." : "Wygeneruj"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Propose Question Dialog (teacher-only) */}
        <Dialog open={proposeOpen} onOpenChange={(o) => {
          setProposeOpen(o);
          if (!o) { setCorrectionOf(null); setCorrectionNote(""); }
        }}>
          <DialogContent className="brut bg-white rounded-md border-2 border-[#0F172A] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display font-black text-2xl">
                {correctionOf ? "🔧 Zgłoś poprawkę pytania" : "🎁 Zaproponuj pytanie do wspólnej puli"}
              </DialogTitle>
              <div className="text-sm text-[#64748B] pt-1">
                {correctionOf
                  ? "Formularz został wypełniony treścią oryginału. Popraw co uważasz, opisz w polu poniżej dlaczego. Po akceptacji administratora oryginał zostanie ZAKTUALIZOWANY."
                  : "Twoja propozycja trafi do administratora. Po akceptacji pytanie stanie się widoczne dla wszystkich nauczycieli."}
              </div>
            </DialogHeader>
            {correctionOf && (
              <div className="bg-[#F1F5F9] border-2 border-[#0F172A] rounded-md p-3 text-xs">
                <div className="font-black uppercase mb-1 tracking-wider text-[#64748B]">Oryginał</div>
                <div className="line-clamp-3">{correctionOf.originalText}</div>
              </div>
            )}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Select value={proposeForm.type} onValueChange={(v) => setProposeForm({ ...proposeForm, type: v })}>
                  <SelectTrigger data-testid="propose-type-select" className="brut border-2 border-[#0F172A] rounded-md font-bold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                    <SelectItem value="mc">ABCD</SelectItem>
                    <SelectItem value="open">Otwarte</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={proposeForm.scope} onValueChange={(v) => setProposeForm({ ...proposeForm, scope: v })}>
                  <SelectTrigger data-testid="propose-scope-select" className="brut border-2 border-[#0F172A] rounded-md font-bold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md max-h-[300px]">
                    {scopes.map((s) => (
                      <SelectItem key={s.scope} value={s.scope}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Temat (np. Roztwory)"
                value={proposeForm.topic}
                onChange={(e) => setProposeForm({ ...proposeForm, topic: e.target.value })}
                className="brut-input border-2 border-[#0F172A] rounded-md h-11"
                data-testid="propose-topic-input"
              />
              <Textarea
                placeholder="Treść pytania…"
                value={proposeForm.text}
                onChange={(e) => setProposeForm({ ...proposeForm, text: e.target.value })}
                className="brut-input border-2 border-[#0F172A] rounded-md min-h-[80px]"
                data-testid="propose-text-textarea"
              />
              {proposeForm.type === "mc" ? (
                <>
                  {["A", "B", "C", "D"].map((letter, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="w-6 font-black">{letter})</span>
                      <Input
                        value={proposeForm.options[i] || ""}
                        onChange={(e) => {
                          const opts = [...(proposeForm.options || ["", "", "", ""])];
                          opts[i] = e.target.value;
                          setProposeForm({ ...proposeForm, options: opts });
                        }}
                        className="brut-input border-2 border-[#0F172A] rounded-md h-10"
                        data-testid={`propose-option-${letter.toLowerCase()}`}
                      />
                    </div>
                  ))}
                  <Select value={proposeForm.correct} onValueChange={(v) => setProposeForm({ ...proposeForm, correct: v })}>
                    <SelectTrigger className="brut border-2 border-[#0F172A] rounded-md font-bold" data-testid="propose-correct-select">
                      <SelectValue placeholder="Poprawna odpowiedź" />
                    </SelectTrigger>
                    <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                      {["A", "B", "C", "D"].map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <div>
                  <Input
                    placeholder="Poprawna odpowiedź — np. H2O | woda"
                    value={proposeForm.correct}
                    onChange={(e) => setProposeForm({ ...proposeForm, correct: e.target.value })}
                    className="brut-input border-2 border-[#0F172A] rounded-md h-11"
                    data-testid="propose-correct-input"
                  />
                  <div className="text-xs text-[#64748B] mt-1.5">
                    💡 Kilka poprawnych: oddziel <code className="bg-[#F1F5F9] px-1 rounded">|</code> lub słowem <code className="bg-[#F1F5F9] px-1 mx-1 rounded">lub</code>
                  </div>
                </div>
              )}
              <Textarea
                placeholder="Wyjaśnienie (opcjonalne)"
                value={proposeForm.explanation}
                onChange={(e) => setProposeForm({ ...proposeForm, explanation: e.target.value })}
                className="brut-input border-2 border-[#0F172A] rounded-md min-h-[60px]"
                data-testid="propose-explanation-textarea"
              />
              {correctionOf && (
                <Textarea
                  placeholder="Dlaczego zgłaszasz tę poprawkę? (opcjonalne, max 500 znaków)"
                  value={correctionNote}
                  maxLength={500}
                  onChange={(e) => setCorrectionNote(e.target.value)}
                  className="brut-input border-2 border-[#B45309] rounded-md min-h-[60px] bg-[#FEF3C7]"
                  data-testid="correction-note-textarea"
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setProposeOpen(false)}
                      className="brut border-2 border-[#0F172A] rounded-md font-bold"
                      data-testid="propose-cancel-btn">
                Anuluj
              </Button>
              <Button onClick={submitProposal} disabled={proposeBusy}
                      className="brut brut-hover bg-[#D1FAE5] hover:bg-[#D1FAE5] text-[#065F46] font-bold rounded-md"
                      data-testid="propose-submit-btn">
                <Gift className="w-4 h-4 mr-2" strokeWidth={2.5} />
                {proposeBusy ? "Wysyłam…" : (correctionOf ? "Wyślij poprawkę" : "Wyślij propozycję")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="delete-question-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Usunąć pytanie?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && <>„{toDelete.text}" — zostanie trwale usunięte z puli. Tej akcji nie można cofnąć.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="delete-question-cancel-btn"
            >
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md"
              data-testid="delete-question-confirm-btn"
            >
              Tak, usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
